import { merge, uniqueId } from 'lodash';
import React from 'react';
import Checkbox from '../../../CommonComponents/CheckBox';
import StyledMaskedInput from '../../../CommonComponents/MaskedInput';
import { Frequency, MatchType, TaxStatus, Unit } from '../AccountWorklow.meta';
import InputDollar from '../../InputDollar/InputDollarView';
import InputPercentage from '../../InputPercentage/InputPercentageView';
import ContributionPicker from '../../SharedComponents/ContributionPicker/ContributionPickerView';
import { AddTier, Label, Percent, RemoveTier, TierInput, Wrapper } from './Contributions.styles';
import { ROTH_ACCOUNT_TYPES } from '../../../Profile/Accounts/Features/AccountPreferences/components/AccountForm/AccountForm.meta';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { IconButton } from '../../../CommonComponents/OPL/IconButton/IconButton';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import SimpleSelect from '../../SimpleSelect/SimpleSelectView';
import { DOLLAR_CONTRIBUTION, PERCENTAGE_CONTRIBUTION } from '../AccountsWorkflowConstants';
import { AccountType } from '../../../Profile/Accounts/api/useAccountsOptionsQuery';

const PercentageInput = ({ id, label, ...other }) => (
  <TierInput htmlFor={id}>
    {label && <Label>{label}</Label>}
    <StyledMaskedInput.Percentage id={id} label={label} {...other} />
    <Percent>%</Percent>
  </TierInput>
);

export const Contributions = ({
  account,
  hasContributionPeriodOption,
  contributionLabel,
  clientId,
  canEmployeeMaxContribution,
  percentageContributionTitle,
  hasContributionPercentOption,
  isEmployerMatchableRetirementAccount,
  updateDraftAccount,
  accountTypeOption,
}) => {
  const defaultMatchTier = { Match: 0, Limit: 0 };

  const defaultContribution = {
    PersonalTaxStatus: TaxStatus.Traditional,
    PersonalUnit: Unit.Dollar,
    PersonalFrequency: hasContributionPeriodOption ? Frequency.Monthly : null,
    PersonalMaximize: false,
    EmployerTaxStatus: TaxStatus.Traditional,
  };

  const contributions =
    account.Contributions.length > 0
      ? account.Contributions
      : [{ ...defaultContribution, Id: uniqueId() }];

  const createContribution = () => {
    updateDraftAccount(
      {
        Contributions: [...contributions, { ...defaultContribution, Id: uniqueId() }],
      },
      clientId,
    );
  };

  const deleteContribution = id => {
    const index = contributions.findIndex(c => c.Id === id);

    updateDraftAccount({
      Contributions: [...contributions.slice(0, index), ...contributions.slice(index + 1)],
    });
  };

  const updateContribution = (id, updates) => {
    const index = contributions.findIndex(c => c.Id === id);

    updateDraftAccount({
      Contributions: [
        ...contributions.slice(0, index),
        merge(contributions[index], updates),
        ...contributions.slice(index + 1),
      ],
    });
  };

  const createMatchTier = contributionId => {
    const contribution = contributions.find(c => c.Id === contributionId);

    updateContribution(contributionId, {
      EmployerMatchType: MatchType.MultiTier,
      EmployerMultiTierMatch: [
        ...(contribution.EmployerMultiTierMatch ?? []),
        { ...defaultMatchTier, Id: uniqueId() },
      ],
    });
  };

  const deleteMatchTier = (contributionId, matchTierId) => {
    const contribution = contributions.find(c => c.Id === contributionId);
    const matchTierIndex = contribution.EmployerMultiTierMatch.findIndex(t => t.Id === matchTierId);
    const index = contributions.findIndex(c => c.Id === contributionId);

    updateDraftAccount({
      Contributions: [
        ...contributions.slice(0, index),
        {
          ...contributions[index],
          EmployerMultiTierMatch: [
            ...contribution.EmployerMultiTierMatch.slice(0, matchTierIndex),
            ...contribution.EmployerMultiTierMatch.slice(matchTierIndex + 1),
          ],
        },
        ...contributions.slice(index + 1),
      ],
    });
  };

  const resetMatchTier = contributionId => {
    const index = contributions.findIndex(c => c.Id === contributionId);

    updateDraftAccount({
      Contributions: [
        ...contributions.slice(0, index),
        { ...contributions[index], EmployerMatchType: null, EmployerMultiTierMatch: [] },
        ...contributions.slice(index + 1),
      ],
    });
  };

  const updateMatchTier = (contributionId, matchTierId, updates) => {
    const contribution = contributions.find(c => c.Id === contributionId);
    const matchTierIndex = contribution.EmployerMultiTierMatch.findIndex(t => t.Id === matchTierId);

    updateContribution(contributionId, {
      EmployerMultiTierMatch: [
        ...contribution.EmployerMultiTierMatch.slice(0, matchTierIndex),
        {
          ...contribution.EmployerMultiTierMatch[matchTierIndex],
          ...updates,
        },
        ...contribution.EmployerMultiTierMatch.slice(matchTierIndex + 1),
      ],
    });
  };

  const isRothAccount = ROTH_ACCOUNT_TYPES.includes(account.AccountType);

  if (!accountTypeOption) {
    return null;
  }

  const contributionTaxStatusOptions = accountTypeOption.extra.ContributionTaxStatuses.map(opt => ({
    label: opt.Label,
    value: opt.Value,
  }));

  const employerContributionTaxStatusOptions = accountTypeOption.extra.EmployerContributionTaxStatuses.map(
    opt => ({
      label: opt.Label,
      value: opt.Value,
    }),
  );

  return (
    <>
      {contributions.map((contribution, contributionIndex) => (
        <>
          <li className="form-input contribuion">
            {account.AccountType >= 0 && hasContributionPeriodOption && (
              <>
                <Box display="flex" justifyContent="space-between">
                  <h2 className="list-title" style={{ marginLeft: 24 }}>
                    {contributionLabel}
                  </h2>
                  {contributionIndex === 0 && isRothAccount && (
                    <Button
                      variant="link"
                      leftIcon="far fa-plus"
                      css="margin: 16px 8px;"
                      onClick={createContribution}
                    >
                      Add Contribution
                    </Button>
                  )}

                  {contributionIndex > 0 && isRothAccount && (
                    <IconButton
                      variant="link"
                      icon="far fa-trash"
                      css="margin: 8px;"
                      aria-label="delete contribution"
                      onClick={() => deleteContribution(contribution.Id)}
                    />
                  )}
                </Box>

                <ul className="horizontal school-list contributions-period-list">
                  <li className="school-item radio">
                    <div
                      onKeyDown={() => {}}
                      role="button"
                      tabIndex={-1}
                      onClick={() => {
                        updateContribution(contribution.Id, {
                          PersonalFrequency: Frequency.Monthly,
                          PersonalMaximize: false,
                        });
                      }}
                    >
                      <div
                        className={`school-item__button-radio-empty ${
                          contribution.PersonalFrequency === Frequency.Monthly &&
                          !(contribution.PersonalMaximize && canEmployeeMaxContribution)
                            ? 'selected'
                            : ''
                        }`}
                      />
                      <button type="button" className="school-item__button small">
                        Monthly
                      </button>
                    </div>
                  </li>
                  <li className="school-item radio">
                    <div
                      onKeyDown={() => {}}
                      role="button"
                      tabIndex={-1}
                      onClick={() => {
                        updateContribution(contribution.Id, {
                          PersonalFrequency: Frequency.Annually,
                          PersonalMaximize: false,
                        });
                      }}
                    >
                      <div
                        className={`school-item__button-radio-empty ${
                          contribution.PersonalFrequency === Frequency.Annually &&
                          !(contribution.PersonalMaximize && canEmployeeMaxContribution)
                            ? 'selected'
                            : ''
                        }`}
                      />
                      <button type="button" className="school-item__button small">
                        Annually
                      </button>
                    </div>
                  </li>

                  {account && canEmployeeMaxContribution && (
                    <li className="school-item radio">
                      <div
                        onKeyDown={() => {}}
                        role="button"
                        tabIndex={-1}
                        onClick={() => {
                          updateContribution(contribution.Id, {
                            PersonalMaximize: true,
                          });
                        }}
                      >
                        <div
                          className={`school-item__button-radio-empty ${
                            contribution.PersonalMaximize ? 'selected' : ''
                          }`}
                        />
                        <button type="button" className="school-item__button small">
                          Maximize
                        </button>
                      </div>
                    </li>
                  )}
                </ul>
              </>
            )}

            <div className="contribution-type">
              {contribution.PersonalUnit === Unit.Dollar && (
                <InputDollar
                  disabled={contribution.PersonalMaximize && canEmployeeMaxContribution}
                  maxLength="256"
                  placeholder={hasContributionPeriodOption ? '' : contributionLabel}
                  id="adv-amount"
                  className="accounts"
                  isLeftAligned
                  value={contribution.PersonalAmount}
                  onChange={({ target: { value } }) => {
                    updateContribution(contribution.Id, {
                      PersonalAmount: value,
                    });
                  }}
                />
              )}
              {contribution.PersonalUnit === Unit.Percentage && (
                <InputPercentage
                  disabled={contribution.PersonalMaximize && canEmployeeMaxContribution}
                  placeholder={hasContributionPeriodOption ? '' : percentageContributionTitle}
                  id="adv-percentage"
                  maxLength="256"
                  numDecimals={0}
                  className="accounts"
                  isLeftAligned
                  value={contribution.PersonalAmount}
                  onChange={({ target: { value } }) => {
                    updateContribution(contribution.Id, {
                      PersonalAmount: value,
                    });
                  }}
                />
              )}
              {hasContributionPercentOption && account.AccountType >= 0 && (
                <ContributionPicker
                  disabled={contribution.PersonalMaximize && canEmployeeMaxContribution}
                  contributionType={
                    contribution.PersonalUnit === Unit.Dollar
                      ? DOLLAR_CONTRIBUTION
                      : PERCENTAGE_CONTRIBUTION
                  }
                  onDollarClick={() => {
                    updateContribution(contribution.Id, {
                      PersonalUnit: Unit.Dollar,
                      PersonalAmount: '',
                    });
                  }}
                  onPercentClick={() => {
                    updateContribution(contribution.Id, {
                      PersonalUnit: Unit.Percentage,
                      PersonalAmount: '',
                    });
                  }}
                />
              )}
            </div>

            {isRothAccount && (
              <div className="adv-standalone-select">
                <SimpleSelect
                  placeholder="Tax Status"
                  id="adv-tax-status"
                  className="no-horizontal-scroll sm"
                  value={contributionTaxStatusOptions.find(
                    opt => opt.value === contribution.PersonalTaxStatus,
                  )}
                  options={contributionTaxStatusOptions}
                  onValueChange={opt =>
                    updateContribution(contribution.Id, { PersonalTaxStatus: opt.value })
                  }
                  isLeftAligned
                />
              </div>
            )}
          </li>

          {isEmployerMatchableRetirementAccount && (
            <>
              <li className="form-input accounts-add-edit">
                <Wrapper>
                  <Checkbox
                    id="employer-match"
                    controlled
                    small
                    checked={(contribution.EmployerMultiTierMatch ?? []).length > 0}
                    onChange={({ target: { checked } }) => {
                      if (checked) {
                        createMatchTier(contribution.Id);
                      } else {
                        resetMatchTier(contribution.Id);
                      }
                    }}
                  >
                    Employer Match
                  </Checkbox>
                  {(contribution.EmployerMultiTierMatch ?? []).length > 0 && (
                    <div data-testid="employer-match-section">
                      {(contribution.EmployerMultiTierMatch ?? []).map((tier, index) => (
                        <div key={tier.Id}>
                          <PercentageInput
                            id={`match-${tier.Id}`}
                            label="Match:"
                            value={tier.Match}
                            onChange={({ target: { value } }) => {
                              updateMatchTier(contribution.Id, tier.Id, { Match: value });
                            }}
                          />
                          <PercentageInput
                            id={`limit-${index}`}
                            label={index === 0 ? 'Up to:' : 'Next:'}
                            value={tier.Limit}
                            onChange={({ target: { value } }) => {
                              updateMatchTier(contribution.Id, tier.Id, { Limit: value });
                            }}
                          />
                          {index > 0 && index === contribution.EmployerMultiTierMatch.length - 1 && (
                            <RemoveTier
                              type="button"
                              onClick={() => {
                                deleteMatchTier(contribution.Id, tier.Id);
                              }}
                              data-testid="remove-tier-button"
                              aria-label="remove tier"
                            >
                              <i className="fas fa-times" />
                            </RemoveTier>
                          )}
                        </div>
                      ))}

                      <AddTier
                        type="button"
                        onClick={() => {
                          createMatchTier(contribution.Id);
                        }}
                        data-testid="add-tier-button"
                      >
                        + Add tier
                      </AddTier>

                      {account.AccountType !== AccountType.SimpleIra && isRothAccount && (
                        <div className="adv-standalone-select">
                          <SimpleSelect
                            placeholder="Tax Status"
                            id="adv-tax-status"
                            className="no-horizontal-scroll sm standalone"
                            value={employerContributionTaxStatusOptions.find(
                              opt => opt.value === contribution.EmployerTaxStatus,
                            )}
                            options={employerContributionTaxStatusOptions}
                            onValueChange={opt =>
                              updateContribution(contribution.Id, { EmployerTaxStatus: opt.value })
                            }
                            isLeftAligned
                          />
                        </div>
                      )}
                    </div>
                  )}
                </Wrapper>
              </li>
            </>
          )}
        </>
      ))}

      {isEmployerMatchableRetirementAccount && (
        <li className="form-input accounts-add-edit" style={{ textAlign: 'left', marginLeft: 24 }}>
          <Checkbox
            id="non-elective-contribution"
            controlled
            small
            onChange={({ target: { checked } }) => {
              updateDraftAccount({ EmployerNonElectiveContribution: checked ? '0' : null });
            }}
            checked={
              account.EmployerNonElectiveContribution !== undefined &&
              account.EmployerNonElectiveContribution !== null &&
              account.EmployerNonElectiveContribution !== 0
            }
          >
            Non-Elective Employer Contribution
          </Checkbox>

          {account.EmployerNonElectiveContribution !== undefined &&
            account.EmployerNonElectiveContribution !== null &&
            account.EmployerNonElectiveContribution !== 0 && (
              <>
                <PercentageInput
                  id="EmployerMatchView_non-elective"
                  label=""
                  value={account.EmployerNonElectiveContribution}
                  onChange={({ target: { value } }) => {
                    updateDraftAccount({ EmployerNonElectiveContribution: value });
                  }}
                />

                {account.AccountType !== AccountType.SimpleIra && isRothAccount && (
                  <div css="margin-top: 16px;">
                    <SimpleSelect
                      placeholder="Tax Status"
                      id="adv-tax-status"
                      className="no-horizontal-scroll sm standalone"
                      value={employerContributionTaxStatusOptions.find(
                        opt => opt.value === account.EmployerNonElectiveContributionTaxStatus,
                      )}
                      options={employerContributionTaxStatusOptions}
                      onValueChange={opt =>
                        updateDraftAccount({ EmployerNonElectiveContributionTaxStatus: opt.value })
                      }
                      isLeftAligned
                    />
                  </div>
                )}
              </>
            )}
        </li>
      )}
    </>
  );
};
